/**
 * Lectura de `GET /getmetaconnection` (y del `data` de `POST /connectmeta`) para
 * el checklist de puesta en marcha. Funciones puras. Ver docs/maps/meta-ads.md.
 *
 * `token_expiring_soon` y `sync_running` (Ajuste 1) se leen opcionales: sin
 * ellos todo funciona igual. El modo demo/live **no** viene acá: lo trae
 * `GET /getmetaauthurl` (`mode`).
 */
import { readMetaConnectionWrite } from './metaWriteBlock';

const TIME_ZONE = 'America/Argentina/Cordoba';
const DAY_MS = 24 * 60 * 60 * 1000;

/** Pasos de la fase 1 (`required_for: "results"`), en orden. */
export const META_RESULTS_STEP_KEYS = ['connect', 'ad_account', 'link_units'];

export const META_STEP_TITLES = {
  connect: 'Conectar tu cuenta de Meta',
  ad_account: 'Elegir cuenta publicitaria',
  link_units: 'Vincular campañas con unidades',
};

const STEP_STATUS = {
  connect: ['pending', 'done', 'reconnect'],
  ad_account: ['pending', 'done', 'none_available'],
  link_units: ['pending', 'partial', 'done', 'no_ads'],
};

const STATUSES = ['not_connected', 'active', 'expired', 'error'];

const bool = (value) => value === true;
const count = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
};
const str = (value) => (typeof value === 'string' && value.trim() ? value.trim() : null);

const SYNC_STATES = ['initial_load', 'updating', 'up_to_date', 'error'];
const SYNC_TONES = ['info', 'success', 'warning', 'danger'];

const optionalNumber = (value) => {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * `sync_progress` (Ajuste 7) de getmetaconnection, syncmetanow y sus 429/409.
 * La API arma `message` / `detail` / `tone`: se muestran tal cual. `null` si no
 * viene o no es un objeto.
 *
 * @param {object|null} raw
 * @returns {object|null}
 */
export function normalizeMetaSyncProgress(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const pct = optionalNumber(raw.pct);
  const retryAfterS = optionalNumber(raw.retry_after_s);
  return {
    state: SYNC_STATES.includes(raw.state) ? raw.state : null,
    reason: str(raw.reason),
    message: str(raw.message),
    detail: str(raw.detail),
    tone: SYNC_TONES.includes(raw.tone) ? raw.tone : 'info',
    showProgressBar: bool(raw.show_progress_bar),
    pct: pct == null ? null : Math.min(100, Math.max(0, Math.round(pct))),
    daysLoaded: optionalNumber(raw.days_loaded),
    daysTotal: optionalNumber(raw.days_total),
    accountsTotal: count(raw.accounts_total),
    accountsPending: count(raw.accounts_pending),
    // Opcionales pendientes: nunca se muestran como problema ni «parcial».
    optionalPending: Array.isArray(raw.optional_pending) ? raw.optional_pending : [],
    syncRunning: bool(raw.sync_running),
    // La API siempre lo manda con sync_progress (APIBOT); sin el campo, false (conservador).
    canSync: bool(raw.can_sync),
    shouldContinue: bool(raw.should_continue),
    retryAfterS: retryAfterS != null && retryAfterS >= 0 ? retryAfterS : null,
    nextSyncAllowedAt: str(raw.next_sync_allowed_at),
    lastSyncAt: str(raw.last_sync_at),
    // Ajuste 11: lo decide el servidor. `null` = API vieja (sin el campo) → regla anterior.
    shouldAutoSync: typeof raw.should_auto_sync === 'boolean' ? raw.should_auto_sync : null,
    autoSyncReason: str(raw.auto_sync_reason),
    lastSyncedAt: str(raw.last_synced_at),
    autoSyncStaleMin: optionalNumber(raw.auto_sync_stale_min),
    // Por cuenta (para medir avance: ver metaSyncProgressMetric en useMetaSync).
    accounts: (Array.isArray(raw.accounts) ? raw.accounts : []).map((a) => ({
      id: str(a?.ad_account_id == null ? '' : String(a.ad_account_id)),
      state: str(a?.state),
      daysLoaded: optionalNumber(a?.days_loaded),
      daysTotal: optionalNumber(a?.days_total),
    })),
  };
}

/**
 * `data` de la conexión con defaults seguros. Rol desconocido → sin permisos.
 *
 * @param {object} raw `data` de la respuesta.
 * @returns {object}
 */
export function normalizeMetaConnection(raw) {
  const data = raw && typeof raw === 'object' ? raw : {};
  const status = STATUSES.includes(data.status) ? data.status : 'not_connected';
  const role = data.viewer_role === 'owner' || data.viewer_role === 'editor' ? data.viewer_role : null;
  const syncStatus = ['ok', 'partial', 'error'].includes(data.last_sync_status) ? data.last_sync_status : null;
  return {
    status,
    connected: bool(data.connected) || status === 'active',
    needsReconnect: bool(data.needs_reconnect) || status === 'expired',
    viewerRole: role,
    viewerCanManage: bool(data.viewer_can_manage),
    viewerCanLink: bool(data.viewer_can_link),
    connectedAt: str(data.connected_at),
    tokenExpiresAt: str(data.token_expires_at),
    // La API decide (≤7 días o ya vencido; siempre false si no está activa).
    tokenExpiringSoon: status === 'active' && bool(data.token_expiring_soon),
    lastSyncAt: str(data.last_sync_at),
    lastSyncStatus: syncStatus,
    lastSyncErrorCode: str(data.last_sync_error_code),
    syncRunning: bool(data.sync_running),
    // Ajuste 7: progreso de la carga (banner único). null sin conexión / sin cuentas.
    syncProgress: normalizeMetaSyncProgress(data.sync_progress),
    adAccounts: {
      availableCount: count(data.ad_accounts?.available_count),
      trackedCount: count(data.ad_accounts?.tracked_count),
      // Ajuste 2: seguidas y disponibles sin la carga de 90 días.
      initialLoadPending: bool(data.ad_accounts?.initial_load_pending) || count(data.ad_accounts?.initial_load_pending_count) > 0,
      initialLoadPendingCount: count(data.ad_accounts?.initial_load_pending_count),
    },
    ads: {
      relevantCount: count(data.ads?.relevant_count),
      unlinkedCount: count(data.ads?.unlinked_count),
      unlinkedNewCount: count(data.ads?.unlinked_new_count),
    },
    checklist: Array.isArray(data.checklist) ? data.checklist : [],
    readyForResults: bool(data.progress?.ready_for_results),
    createCampaigns: str(data.features?.create_campaigns),
    // F2a: bloque `write` (null sin la bandera de escritura).
    write: readMetaConnectionWrite(data.write),
  };
}

/**
 * Con Meta conectado y al menos una cuenta seguida (o la puesta en marcha ya
 * completa), el panel abre en Campañas; la puesta en marcha queda solo para
 * conectar / reconectar o elegir cuenta.
 */
export function metaAdsOpensInCampaigns(conn) {
  if (!conn?.connected || conn.needsReconnect) return false;
  return (conn.adAccounts?.trackedCount || 0) > 0 || buildMetaChecklist(conn).complete;
}

/** Estado de un paso cuando la API no manda `checklist` (respuesta vieja o parcial). */
function fallbackStepStatus(key, conn) {
  if (key === 'connect') {
    if (conn.needsReconnect) return 'reconnect';
    return conn.connected ? 'done' : 'pending';
  }
  if (key === 'ad_account') return conn.adAccounts.trackedCount > 0 ? 'done' : 'pending';
  if (conn.ads.unlinkedCount > 0) return 'partial';
  return 'pending';
}

function stepLabel(key, status, conn) {
  if (status === 'done') return key === 'connect' ? 'Conectado' : key === 'ad_account' ? 'Lista' : 'Listo';
  if (status === 'reconnect') return 'Hay que reconectar';
  if (status === 'none_available') return 'Sin cuentas';
  if (status === 'no_ads') return 'Sin anuncios';
  if (status === 'partial') {
    // `ads.unlinked_count` cuenta anuncios (no campañas): se dice así.
    const n = conn.ads.unlinkedCount;
    if (n <= 0) return 'Faltan vincular';
    return `${n} ${n === 1 ? 'anuncio' : 'anuncios'} sin vincular`;
  }
  return 'Pendiente';
}

function stepTone(status) {
  if (status === 'done' || status === 'no_ads') return 'done';
  if (status === 'reconnect' || status === 'none_available') return 'warning';
  if (status === 'partial') return 'partial';
  return 'pending';
}

/**
 * Checklist de la fase 1 (conectar, cuenta, vincular) con etiqueta y tono por
 * paso, más el progreso. `no_ads` cuenta como listo (no hay nada que vincular).
 *
 * @param {object} conn Resultado de `normalizeMetaConnection`.
 * @returns {{ steps: Array<{ key: string, title: string, status: string, label: string, tone: string, done: boolean }>,
 *   done: number, total: number, complete: boolean, createCampaignsLater: boolean }}
 */
export function buildMetaChecklist(conn) {
  const byKey = new Map(
    (conn?.checklist || [])
      .filter((item) => item && typeof item.key === 'string')
      .map((item) => [item.key, item]),
  );
  const steps = META_RESULTS_STEP_KEYS.map((key) => {
    const apiStatus = byKey.get(key)?.status;
    const status = STEP_STATUS[key].includes(apiStatus) ? apiStatus : fallbackStepStatus(key, conn);
    const tone = stepTone(status);
    const fresh = key === 'link_units' ? (conn?.ads?.unlinkedNewCount || 0) : 0;
    return {
      key,
      title: META_STEP_TITLES[key],
      status,
      label: stepLabel(key, status, conn),
      tone,
      done: tone === 'done',
      // «N nuevos»: anuncios nuevos sin vincular en campañas ya vinculadas.
      badge: fresh > 0 ? `${fresh} ${fresh === 1 ? 'nuevo' : 'nuevos'}` : null,
    };
  });
  const done = steps.filter((s) => s.done).length;
  const createCampaignsLater = conn?.createCampaigns === 'coming_soon'
    || [...byKey.values()].some((item) => item.required_for === 'create');
  return { steps, done, total: steps.length, complete: done === steps.length, createCampaignsLater };
}

const dayKey = (date) => new Intl.DateTimeFormat('en-CA', {
  timeZone: TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
}).format(date);

/**
 * Fecha de la última actualización en hora de Córdoba: «hoy 06:00», «ayer 06:00»
 * o «20/10 06:00». Vacío si no hay fecha válida.
 *
 * @param {string|null} iso
 * @param {Date} [now]
 * @returns {string}
 */
export function formatMetaSyncTime(iso, now = new Date()) {
  const at = new Date(iso || '');
  if (!iso || Number.isNaN(at.getTime())) return '';
  const time = new Intl.DateTimeFormat('es-AR', {
    timeZone: TIME_ZONE, hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(at);
  if (dayKey(at) === dayKey(now)) return `hoy ${time}`;
  if (dayKey(at) === dayKey(new Date(now.getTime() - DAY_MS))) return `ayer ${time}`;
  const date = new Intl.DateTimeFormat('es-AR', {
    timeZone: TIME_ZONE, day: '2-digit', month: '2-digit',
  }).format(at);
  return `${date} ${time}`;
}

/**
 * «datos de hoy 12:41» o, si no es de hoy, «datos al 21/10 12:41» (hora de Córdoba).
 * Vacío si no hay fecha válida.
 */
export function formatMetaDataAsOf(iso, now = new Date()) {
  const at = new Date(iso || '');
  if (!iso || Number.isNaN(at.getTime())) return '';
  const time = new Intl.DateTimeFormat('es-AR', {
    timeZone: TIME_ZONE, hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(at);
  if (dayKey(at) === dayKey(now)) return `datos de hoy ${time}`;
  const date = new Intl.DateTimeFormat('es-AR', { timeZone: TIME_ZONE, day: '2-digit', month: '2-digit' }).format(at);
  return `datos al ${date} ${time}`;
}

/**
 * «Actualizado hoy a las 14:05» / «ayer a las …» / «el 21/10 a las …» (hora de
 * Córdoba), para el «Datos al día» del banner (Ajuste 7). Vacío sin fecha válida.
 */
export function formatMetaUpdatedAt(iso, now = new Date()) {
  const at = new Date(iso || '');
  if (!iso || Number.isNaN(at.getTime())) return '';
  const time = new Intl.DateTimeFormat('es-AR', {
    timeZone: TIME_ZONE, hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(at);
  if (dayKey(at) === dayKey(now)) return `Actualizado hoy a las ${time}`;
  if (dayKey(at) === dayKey(new Date(now.getTime() - DAY_MS))) return `Actualizado ayer a las ${time}`;
  const date = new Intl.DateTimeFormat('es-AR', { timeZone: TIME_ZONE, day: '2-digit', month: '2-digit' }).format(at);
  return `Actualizado el ${date} a las ${time}`;
}

/**
 * Frescura de Campañas con el formato de `data_freshness_text` de `getmetacampaigns`
 * (`meta_enrich.py`): «Datos al día · actualizado 19:12» / «· actualizado ayer 19:12» /
 * «· actualizado el 21/10 19:12» (hora de Córdoba). La usa el panel mientras todavía no llegó
 * ese texto (t991u): antes armaba «Datos al día · Actualizado hoy a las 19:12» con el `detail` de
 * `sync_progress` y la línea cambiaba de forma al llegar la lista. Toma la hora del `detail` de
 * la API (mismo `last_sync_at`); si no viene o no se reconoce, de `lastSyncAt`. Sin ninguna: «Datos al día».
 */
const UPDATED_DETAIL_RE = /^Actualizado (?:(hoy)|(ayer)|el (\d{2}\/\d{2})) a las (\d{2}:\d{2})$/;
export function formatMetaCampaignsFreshness({ detail = null, lastSyncAt = null } = {}, now = new Date()) {
  const base = 'Datos al día';
  const m = typeof detail === 'string' ? detail.trim().match(UPDATED_DETAIL_RE) : null;
  if (m) {
    const [, , yesterday, date, time] = m;
    if (date) return `${base} · actualizado el ${date} ${time}`;
    return `${base} · actualizado ${yesterday ? 'ayer ' : ''}${time}`;
  }
  const at = new Date(lastSyncAt || '');
  if (!lastSyncAt || Number.isNaN(at.getTime())) return base;
  const time = new Intl.DateTimeFormat('es-AR', {
    timeZone: TIME_ZONE, hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(at);
  if (dayKey(at) === dayKey(now)) return `${base} · actualizado ${time}`;
  if (dayKey(at) === dayKey(new Date(now.getTime() - DAY_MS))) return `${base} · actualizado ayer ${time}`;
  const date = new Intl.DateTimeFormat('es-AR', { timeZone: TIME_ZONE, day: '2-digit', month: '2-digit' }).format(at);
  return `${base} · actualizado el ${date} ${time}`;
}

/**
 * Ajuste 10, preset «Hoy»: «Datos hasta las 14:05» (hora de Córdoba) si la última
 * sync es de hoy; si no, «Última actualización: ayer 06:00». Vacío sin fecha válida.
 */
export function formatMetaDataUntil(iso, now = new Date()) {
  const at = new Date(iso || '');
  if (!iso || Number.isNaN(at.getTime())) return '';
  if (dayKey(at) !== dayKey(now)) return `Última actualización: ${formatMetaSyncTime(iso, now)}`;
  const time = new Intl.DateTimeFormat('es-AR', {
    timeZone: TIME_ZONE, hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(at);
  return `Datos hasta las ${time}`;
}
